import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { SsosInfoResponse } from 'src/apis/storage/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  url: string;
  label: string;
  fieldName: string;
};

export const ssoOptionSchema = formOptionSchema.merge(
  z.object({ hint: z.string() })
);

type SsoOption = z.input<typeof ssoOptionSchema>;

export const SsoSearchableMultiSelect: FC<Props> = ({
  url,
  label,
  fieldName,
}) => {
  const ssos = useAxios(
    (axios, baseConfig) =>
      axios.request<SsosInfoResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  useEffect(() => {
    ssos.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const ssosSearch = useClientSearch(
    ssos.response?.data._embedded.items.map(
      (sso) =>
        ({
          label: sso.name,
          value: sso.id,
          hint: sso.shortName,
        }) satisfies SsoOption
    ),
    (sso, search) =>
      sso.label.toLowerCase().match(search) ||
      sso.hint.toLowerCase().match(search)
  );

  return (
    <SearchableMultiSelect
      label={label}
      name={fieldName}
      stacked
      placeholder="E.g. SSO-1"
      {...ssosSearch}
    />
  );
};
